import React from 'react';

import { useIntl } from 'react-intl';

import { aiConfigItemMessages } from '@atlaskit/editor-common/ai-messages';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import { pasteOptionsToolbarMessages } from '@atlaskit/editor-common/messages';
import {
	useEditorToolbar,
	AI_PASTE_MENU_SECTION,
	PASTE_MENU,
	PASTE_MENU_RANK,
} from '@atlaskit/editor-common/toolbar';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { PasteOptionsToolbarSharedState } from '@atlaskit/editor-plugin-paste-options-toolbar';
import type { Slice } from '@atlaskit/editor-prosemirror/model';
import {
	AICommandIcon,
	AIShortenIcon,
	AISpellcheckIcon,
	ToolbarDropdownItem,
	ToolbarDropdownItemSection,
} from '@atlaskit/editor-toolbar';
import type { RegisterComponent } from '@atlaskit/editor-ui-control-model';
import AiGenerativeCleanupIcon from '@atlaskit/icon-lab/core/ai-generative-cleanup';
import type { NewCoreIconProps } from '@atlaskit/icon/types';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { expValNoExposure } from '@atlaskit/tmp-editor-statsig/expVal';

import type { AIPlugin } from '../../aiPluginType';
import { createOpenAIModalCommand } from '../../pm-plugins/decoration/actions';
import type { AIGlobalOptIn } from '../../types/types';
import type { EditorPluginAIConfigItem } from '../prebuilt/config-items/config-items';
import {
	fixSpellingAndGrammar,
	improveWriting,
} from '../prebuilt/config-items/prompts/enhance/enhance';
import { makeShorter } from '../prebuilt/config-items/prompts/make-shorter/make-shorter';
import { addPolish } from '../prebuilt/config-items/prompts/polish/polish';

const addPolishWithRename = () => {
	const item = addPolish();
	if (expValEquals('platform_editor_ai_rename_add_polish', 'isEnabled', true)) {
		item.title = aiConfigItemMessages.improveFormattingTitle;
		item.description = aiConfigItemMessages.improveFormattingDescription;
	}
	return item;
};

type AIPasteMenuItemProps = {
	aiGlobalOptIn: AIGlobalOptIn;
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	configItem: EditorPluginAIConfigItem;
	icon: {
		(props: NewCoreIconProps): JSX.Element;
		displayName: string;
	};
	testId: string;
};

const AIPasteMenuItem = ({
	aiGlobalOptIn,
	api,
	configItem,
	icon,
	testId,
}: AIPasteMenuItemProps) => {
	const { editorView } = useEditorToolbar();
	const { formatMessage, locale } = useIntl();

	const { pasteStartPos, pasteEndPos } = useSharedPluginStateWithSelector(
		api,
		['pasteOptionsToolbarPlugin'],
		(states) => {
			const pluginState = states.pasteOptionsToolbarPluginState as
				| PasteOptionsToolbarSharedState
				| undefined;
			return {
				pasteStartPos: pluginState?.pasteStartPos ?? 0,
				pasteEndPos: pluginState?.pasteEndPos ?? 0,
			};
		},
	);

	const Icon = icon as React.ComponentType<NewCoreIconProps>;
	const label = formatMessage(configItem.title);

	return (
		<ToolbarDropdownItem
			testId={testId}
			elemBefore={<Icon size="small" label="" />}
			/* eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed) */
			onClick={(event) => {
				if (!editorView) {
					return;
				}
				const isKeyboardTrigger = 'key' in event;
				const triggerMethod = isKeyboardTrigger ? 'keyboard' : 'click';
				const openAIModalCommand = createOpenAIModalCommand({
					state: editorView.state,
					configItem,
					lastTriggeredFrom: 'pasteActionsMenu',
					triggerMethod,
					aiGlobalOptIn,
					locale,
					overrideFromPos: pasteStartPos,
					overrideToPos: pasteEndPos,
					shouldExpandPositions: false,
				});
				openAIModalCommand(editorView.state, editorView.dispatch);
			}}
		>
			{label}
		</ToolbarDropdownItem>
	);
};

const AIPasteMenuSection = ({ children }: { children?: React.ReactNode }) => {
	const intl = useIntl();
	return (
		<ToolbarDropdownItemSection
			title={intl.formatMessage(pasteOptionsToolbarMessages.pasteMenuActionsTitle)}
		>
			{children}
		</ToolbarDropdownItemSection>
	);
};

export const AI_PASTE_MENU_KEY = 'ai-paste-menu';
const AI_PASTE_MENU_SECTION_KEY = AI_PASTE_MENU_SECTION.key;
const AI_PASTE_MENU_IMPROVE_WRITING_KEY = 'ai-paste-menu-improve-writing';
const AI_PASTE_MENU_FIX_SPELLING_KEY = 'ai-paste-menu-fix-spelling-and-grammar';
const AI_PASTE_MENU_ADD_POLISH_KEY = 'ai-paste-menu-add-polish';
const AI_PASTE_MENU_MAKE_SHORTER_KEY = 'ai-paste-menu-make-shorter';

type AIPasteMenuButtonConfig = {
	button: string;
	charMaxThreshold?: number;
	charMinThreshold: number;
	rank: number;
};

export const AI_PASTE_MENU_DISABLED_IN_NODES: string[] = ['codeBlock', 'heading'];

/**
 * Returns the active excluded nodes list, preferring the value from the `excludedNodes`
 * experiment param when it is non-empty and contains a valid string[]. Falls back to
 * `AI_PASTE_MENU_DISABLED_IN_NODES` otherwise.
 */
export const getActiveDisabledInNodes = (): string[] => {
	const raw = expValNoExposure('platform_editor_paste_actions_menu', 'excludedNodes', '');
	if (!raw) {
		return AI_PASTE_MENU_DISABLED_IN_NODES;
	}
	try {
		const parsed: unknown = JSON.parse(raw);
		if (Array.isArray(parsed) && parsed.length > 0 && parsed.every((n) => typeof n === 'string')) {
			return parsed as string[];
		}
	} catch {
		// Invalid JSON – fall through to default.
	}
	return AI_PASTE_MENU_DISABLED_IN_NODES;
};

export const AI_PASTE_MENU_CONFIG: AIPasteMenuButtonConfig[] = [
	{
		button: AI_PASTE_MENU_IMPROVE_WRITING_KEY,
		charMinThreshold: 100,
		rank: 2,
	},
	{
		button: AI_PASTE_MENU_FIX_SPELLING_KEY,
		charMinThreshold: 100,
		rank: 3,
	},
	{
		button: AI_PASTE_MENU_MAKE_SHORTER_KEY,
		charMinThreshold: 100,
		charMaxThreshold: 600,
		rank: 4,
	},
	{
		button: AI_PASTE_MENU_ADD_POLISH_KEY,
		charMinThreshold: 601,
		rank: 1,
	},
];

const isAIPasteMenuButtonConfig = (value: unknown): value is AIPasteMenuButtonConfig =>
	typeof value === 'object' &&
	value !== null &&
	typeof (value as AIPasteMenuButtonConfig).button === 'string' &&
	typeof (value as AIPasteMenuButtonConfig).charMinThreshold === 'number' &&
	(typeof (value as AIPasteMenuButtonConfig).charMaxThreshold === 'number' ||
		(value as AIPasteMenuButtonConfig).charMaxThreshold === undefined) &&
	typeof (value as AIPasteMenuButtonConfig).rank === 'number';

/**
 * Returns the active menu config, preferring the value from the `menuConfig` experiment
 * param when it is non-empty and contains a valid `AIPasteMenuButtonConfig[]`. Falls back
 * to the hardcoded `AI_PASTE_MENU_CONFIG` otherwise.
 */
export const getActiveMenuConfig = (): AIPasteMenuButtonConfig[] => {
	const raw = expValNoExposure('platform_editor_paste_actions_menu', 'menuConfig', '');
	if (!raw) {
		return AI_PASTE_MENU_CONFIG;
	}
	try {
		const parsed: unknown = JSON.parse(raw);
		if (Array.isArray(parsed) && parsed.length > 0 && parsed.every(isAIPasteMenuButtonConfig)) {
			return parsed;
		}
	} catch {
		// Invalid JSON – fall through to default.
	}
	return AI_PASTE_MENU_CONFIG;
};

/**
 * Returns true when the pasted text does not look like prose — i.e. it contains
 * no whitespace AND is entirely ASCII characters (after trimming).
 *
 * Text without spaces that is all-ASCII is very likely a URL, file path, token,
 * hash, or variable name — content where AI writing suggestions (improve writing,
 * fix spelling, etc.) would not be useful.
 *
 * We require all-ASCII because many natural languages (Chinese, Japanese, Thai,
 * Lao, Khmer, etc.) do not use spaces between words, so a no-whitespace check
 * alone would incorrectly flag those as non-prose.
 */
const hasTableNode = (slice: Slice | undefined): boolean => {
	if (!slice) {
		return false;
	}
	let found = false;
	slice.content.descendants((node) => {
		if (node.type.name === 'table') {
			found = true;
			return false;
		}
		return true;
	});
	return found;
};

export const isNotProse = (text: string): boolean => {
	const trimmed = text.trim();
	if (!trimmed) {
		return false;
	}

	// Check each character: if we find whitespace it's prose-like,
	// if we find a non-ASCII character it's likely CJK/Thai/etc.
	for (let i = 0; i < trimmed.length; i++) {
		const code = trimmed.charCodeAt(i);
		// Whitespace (space, tab, newline, etc.) → prose-like
		if (code === 0x20 || code === 0x09 || code === 0x0a || code === 0x0d) {
			return false;
		}
		// Non-ASCII character → likely a non-Latin script (CJK, Thai, etc.)
		if (code > 0x7f) {
			return false;
		}
	}

	// No whitespace and all ASCII → URL, token, path, etc.
	return true;
};

export const getAIPasteMenuComponents = ({
	aiGlobalOptIn,
	api,
}: {
	aiGlobalOptIn: AIGlobalOptIn;
	api: ExtractInjectionAPI<AIPlugin> | undefined;
}): RegisterComponent[] => {
	const activeConfig = getActiveMenuConfig();

	const getPlaintextLength = () => {
		const state = api?.pasteOptionsToolbarPlugin?.sharedState.currentState() as
			| PasteOptionsToolbarSharedState
			| undefined;
		return state?.plaintextLength ?? 0;
	};

	const getAncestorNodeNames = (): string[] => {
		const state = api?.pasteOptionsToolbarPlugin?.sharedState.currentState() as
			| PasteOptionsToolbarSharedState
			| undefined;
		return state?.pasteAncestorNodeNames ?? [];
	};

	const getPastedText = (): string | undefined => {
		const state = api?.paste?.sharedState.currentState();
		return state?.lastContentPasted?.text;
	};

	const getPastedSlice = () => {
		const state = api?.paste?.sharedState.currentState();
		return state?.lastContentPasted?.pastedSlice;
	};

	const configByKey = Object.fromEntries(activeConfig.map((c) => [c.button, c]));

	// When the v2 experiment is enabled, delegate all visibility checks to the
	// rule registry exposed by the paste-options-toolbar plugin. Otherwise fall
	// back to the original logic.
	const rules = expValEquals('platform_editor_paste_actions_menu_v2_boolean', 'isEnabled', true)
		? api?.pasteOptionsToolbarPlugin?.actions.getPasteMenuRules()
		: undefined;

	const makeIsHidden = (key: string) => () => {
		const config = configByKey[key];
		if (!config) {
			return true;
		}

		if (rules) {
			const { allRules, minCharsRule, excludedAncestorRule, notProseRule, containsTableRule } =
				rules;

			const aboveMax = (): boolean =>
				config.charMaxThreshold !== undefined &&
				(api?.pasteOptionsToolbarPlugin?.sharedState?.currentState()?.plaintextLength || 0) >
					config.charMaxThreshold;

			return allRules(
				minCharsRule(config.charMinThreshold),
				excludedAncestorRule(getActiveDisabledInNodes()),
				notProseRule,
				containsTableRule,
				aboveMax,
			)();
		} else {
			const length = getPlaintextLength();
			const ancestorNodeNames = getAncestorNodeNames();
			const belowMin = length < config.charMinThreshold;
			const aboveMax = config.charMaxThreshold !== undefined && length > config.charMaxThreshold;
			const inDisabledNode = ancestorNodeNames.some((name) =>
				getActiveDisabledInNodes().includes(name),
			);
			const pastedText = getPastedText();
			const notProse = pastedText !== undefined && isNotProse(pastedText);
			const containsTable = hasTableNode(getPastedSlice());
			return belowMin || aboveMax || inDisabledNode || notProse || containsTable;
		}
	};

	return [
		{
			type: 'menu-section',
			key: AI_PASTE_MENU_SECTION_KEY,
			parents: [
				{
					type: PASTE_MENU.type,
					key: PASTE_MENU.key,
					rank: PASTE_MENU_RANK[AI_PASTE_MENU_SECTION_KEY],
				},
			],
			component: AIPasteMenuSection,
		},
		{
			type: 'menu-item',
			key: AI_PASTE_MENU_IMPROVE_WRITING_KEY,
			parents: [
				{
					type: 'menu-section',
					key: AI_PASTE_MENU_SECTION_KEY,
					rank: configByKey[AI_PASTE_MENU_IMPROVE_WRITING_KEY]?.rank ?? 0,
				},
			],
			isHidden: makeIsHidden(AI_PASTE_MENU_IMPROVE_WRITING_KEY),
			component: () => (
				<AIPasteMenuItem
					aiGlobalOptIn={aiGlobalOptIn}
					api={api}
					configItem={improveWriting()}
					icon={AICommandIcon}
					testId="ai-paste-menu__improve-writing"
				/>
			),
		},
		{
			type: 'menu-item',
			key: AI_PASTE_MENU_FIX_SPELLING_KEY,
			parents: [
				{
					type: 'menu-section',
					key: AI_PASTE_MENU_SECTION_KEY,
					rank: configByKey[AI_PASTE_MENU_FIX_SPELLING_KEY]?.rank ?? 0,
				},
			],
			isHidden: makeIsHidden(AI_PASTE_MENU_FIX_SPELLING_KEY),
			component: () => (
				<AIPasteMenuItem
					aiGlobalOptIn={aiGlobalOptIn}
					api={api}
					configItem={fixSpellingAndGrammar()}
					icon={AISpellcheckIcon}
					testId="ai-paste-menu__fix-spelling-and-grammar"
				/>
			),
		},
		{
			type: 'menu-item',
			key: AI_PASTE_MENU_ADD_POLISH_KEY,
			parents: [
				{
					type: 'menu-section',
					key: AI_PASTE_MENU_SECTION_KEY,
					rank: configByKey[AI_PASTE_MENU_ADD_POLISH_KEY]?.rank ?? 0,
				},
			],
			isHidden: makeIsHidden(AI_PASTE_MENU_ADD_POLISH_KEY),
			component: () => (
				<AIPasteMenuItem
					aiGlobalOptIn={aiGlobalOptIn}
					api={api}
					configItem={addPolishWithRename()}
					icon={AiGenerativeCleanupIcon}
					testId="ai-paste-menu__add-polish"
				/>
			),
		},
		{
			type: 'menu-item',
			key: AI_PASTE_MENU_MAKE_SHORTER_KEY,
			parents: [
				{
					type: 'menu-section',
					key: AI_PASTE_MENU_SECTION_KEY,
					rank: configByKey[AI_PASTE_MENU_MAKE_SHORTER_KEY]?.rank ?? 0,
				},
			],
			isHidden: makeIsHidden(AI_PASTE_MENU_MAKE_SHORTER_KEY),
			component: () => (
				<AIPasteMenuItem
					aiGlobalOptIn={aiGlobalOptIn}
					api={api}
					configItem={makeShorter()}
					icon={AIShortenIcon}
					testId="ai-paste-menu__make-shorter"
				/>
			),
		},
	];
};
